import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Target } from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { analysisService } from '../services/api';
import { formatCurrency, formatPercent, getRecommendationColor, getRecommendationText } from '../utils/helpers';

const AIRecommendationsPage = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadRecommendations = async () => {
      try {
        // ✨ 새로운 API 사용: ai_portfolio_priority 테이블 기반
        const response = await analysisService.getAIPortfolioPriority();
        setRecommendations(response.data.data || []);
      } catch (error) {
        console.error('AI Recommendations load error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecommendations();
  }, []);

  if (isLoading) {
    return (
      <Layout>
        <Loading message="AI 추천 데이터를 불러오는 중..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💡 AI 추천</h1>
          <p className="text-gray-600">AI가 분석한 종목 추천을 확인하세요</p>
        </div>

        {/* 추천 요약 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">매수 추천</p>
                <p className="text-xl font-bold text-red-600">
                  {recommendations.filter(r => r.recommendation === 'BUY').length}개
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">매도 추천</p>
                <p className="text-xl font-bold text-blue-600">
                  {recommendations.filter(r => r.recommendation === 'SELL').length}개
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500 transform rotate-180" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">보유 추천</p>
                <p className="text-xl font-bold text-gray-600">
                  {recommendations.filter(r => r.recommendation === 'HOLD').length}개
                </p>
              </div>
              <Target className="w-8 h-8 text-gray-500" />
            </div>
          </div>
        </div>

        {/* 추천 목록 */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">AI 추천 종목</h2>
          </div>
          
          {recommendations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">종목</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">추천</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">신뢰도</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">현재가</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">목표가</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">기대수익률</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recommendations.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.stock_name}</div>
                          <div className="text-sm text-gray-500">{item.stock_code}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRecommendationColor(item.recommendation)}`}>
                          {getRecommendationText(item.recommendation)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatPercent(item.confidence || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.current_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.target_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`${item.expected_return > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                          {formatPercent(item.expected_return || 0)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <Brain className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">AI 추천 데이터가 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AIRecommendationsPage;